import React, { useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import ReCAPTCHA from 'react-google-recaptcha';
import { useMst } from '../../../models/stores/Root';
import FormErrorLabel from '../../../components/input/FormErrorLabel';
import ShippingMethodSection from '../../../components/framework/ShippingMethodSection';
import AccountCreateFormWithFormContext from '../../../components/input/AccountCreateFormWithFormContext';
import AuthenticatedContactSection from '../../../components/framework/AuthenticatedContactSection';
import AuthenticatedShippingAddressSection from '../../../components/framework/AuthenticatedShippingAddressSection';
import AuthenticatedBillingAddressSection from '../../../components/framework/AuthenticatedBillingAddressSection';
import AuthenticatedPaymentSection from '../../../components/framework/AuthenticatedPaymentSection';
import OrderComment from '../../../components/input/OrderComment';
import Button from '../../../components/input/Button';
import SectionTitle from '../../../components/content/SectionTitle';
import CustomMessage from '../../../components/content/CustomMessage';
import InternalLink from '../../../components/input/InternalLink';
import {
  CustomerBillingAddressContract,
  CustomerShippingAddressContract,
} from '../../../types/Customer';
import ApiValidationError from '../../../models/classes/ApiValidationError';
import ServiceError from '../../../models/classes/ServiceError';
import GuestContactSection from '../../../components/framework/GuestContactSection';
import { CustomerLoginFormData } from '../../../components/input/CustomerLoginForm';
import { GuestInfoFormData } from '../../../components/input/GuestInfoForm';
import LabeledCheckbox from '../../../components/input/LabeledCheckbox';
import { AddressContract, AddressFormData } from '../../../types/Address';
import { SelectOption } from '../../../types/common';
import AddressFormWithButtons from '../../../components/input/AddressFormWithButtons';
import { SiteLimitationKeys, UIConstants } from '../../../utils/constants';
import { getCustomFieldsSchemaWithFormContext } from '../../../models/schemas/CustomFields';
import CustomFieldSection from '../../../components/framework/CustomFieldSection';
import { getPasswordStringValidator } from '../../../models/schemas/Generators';
import { PayPalOnApproveData } from '../../../types/PayPal';

export type PayPalViewProps = {
  onLogout: () => void;
  onEditPayment: () => void;
  onScrollTopOfPage: () => void;
};
type Theme = 'light' | 'dark';
type Size = 'compact' | 'normal' | 'invisible';
type Badge = 'bottomright' | 'bottomleft' | 'inline';
interface PayPalFormData {
  createAccount: boolean;
  password?: string;
  customFields?: {
    [x: string]: string;
  };
}

const PayPalView: React.FC<PayPalViewProps> = observer(
  ({ onLogout, onEditPayment, onScrollTopOfPage }: PayPalViewProps) => {
    const rootStore = useMst();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { configuration, customer, cart, currentStep } = rootStore;
    const [formErrors, setFormErrors] = useState<string[]>([]);
    const reCaptchaRef = useRef<ReCAPTCHA>(null);
    // TODO: Remove setters from component and sub-components
    const [, setHasContactInfoError] = useState(false);
    const [, setHasAddressError] = useState(false);
    const [captchaResponse, setCaptchaResponse] = useState<string | null>(null);
    const changeCallback = async (response: string | null): Promise<void> => {
      if (configuration.reCaptcha.size === 'invisible') {
        return;
      }
      setCaptchaResponse(null);
      if (response) {
        const result = await configuration.verifyReCaptchaFlow(response);
        if (result === 200) {
          setCaptchaResponse(response);
          cart.updateSubmitToken(response);
        }
      }
    };
    const expiredCallback = () => setCaptchaResponse(null);
    const [isLoginRequired] = useState(
      !configuration.features.disableCustomerLogin &&
        !configuration.features.enableGuestCheckout
    );

    const countryOptions = configuration.countriesAsSelectOptions;

    const handleGetStateOptions = (
      country: string
    ): { options: SelectOption[]; label: string } => {
      const options = configuration.getCurrentStatesAsSelectOptions(country);
      const label = configuration.getStateLabel(country);
      return { options, label };
    };

    const handleShippingMethodChanged = async (): Promise<void> => {
      cart.setUpdatingCart(true);
      await rootStore.updateCartShippingAddressFlow();
      if (
        configuration.isEnabled(SiteLimitationKeys.STRIPE_SERVICE_ENABLED) &&
        configuration.hasStripePaymentMethodEnabled
      ) {
        await rootStore.putStripeIntentFlow();
      }
      cart.setUpdatingCart(false);
    };

    const handleTryCustomerLoginAsync = async (
      formData: CustomerLoginFormData
    ): Promise<string[] | undefined> => {
      try {
        await rootStore.loginFlow(formData);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.isValidationError && error.errors?.length) {
          return error.errors;
        }
        // eslint-disable-next-line no-console
        console.error(error);
        return ['Unable to login at this time.'];
      }
      return undefined;
    };

    const handleLoginSuccess = async (): Promise<void> => {
      await rootStore.loadCustomerFlow();
      rootStore.send({ type: 'USER_LOGIN', customer: customer.customerData });
      const { payerID, orderID, paymentID } = rootStore.machineState?.context
        .paypalData as PayPalOnApproveData;
      await rootStore.initializePayPalCheckout(
        payerID,
        configuration.features.enablePayPalPpcp ? orderID : paymentID
      );
    };

    const handleValidGuestContactSection = (
      formData: GuestInfoFormData
    ): void => {
      rootStore.setEmailRegistered(formData.emailAddress);
      cart.updateCustomer(formData);
    };

    const handleAddressSave = async (
      formData: AddressFormData
    ): Promise<void> => {
      await rootStore.updatePayPalAddressFlow(formData as AddressContract);
    };

    const handleEmailOptInOnChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ): void => {
      event.persist();
      cart.updateCustomer({ emailOptIn: event.target.checked });
    };

    const handleShippingAddressEdit = async (): Promise<void> => {
      rootStore.send({ type: 'EDIT_SHIPPING' });
    };

    const handleShippingAddressEditCanceled = async (): Promise<void> => {
      rootStore.send({ type: 'EDIT_SHIPPING_CANCELED' });
    };

    const handleStartNewCheckoutClicked = async (): Promise<void> => {
      await rootStore.startNewCheckout();
    };

    const handleApiValidationError = (error: ApiValidationError): void => {
      const errorMessages: string[] = error.extractErrorMessages();

      if (errorMessages[0].includes('TRANSACTION_REFUSED')) {
        setFormErrors([
          'Please try a different payment method. PayPal has errored with the following error: Request was refused.',
        ]);
      } else {
        setFormErrors(errorMessages);
      }

      onScrollTopOfPage();
    };

    const handleGenericError = (error: ServiceError | Error): void => {
      setFormErrors([error.message]);
      onScrollTopOfPage();
    };

    const passwordSchema = yup.object().shape({
      createAccount: yup.boolean(),
      password: yup.string().when('createAccount', {
        is: (createAccount: boolean) =>
          createAccount ||
          (!configuration.features.disableCustomerLogin &&
            !configuration.features.enableGuestCheckout &&
            !customer.isLoggedIn),
        then: getPasswordStringValidator('password', true),
        otherwise: yup.string().optional(),
      }),
    });

    const customFieldsSchema = yup.object().shape({
      customFields: getCustomFieldsSchemaWithFormContext(
        cart.cartData.customFields
      ),
    });

    const payPalViewFormSchema = yup
      .object()
      .concat(passwordSchema)
      .concat(customFieldsSchema);

    const payPalViewForm = useForm({
      resolver: yupResolver(payPalViewFormSchema),
      shouldUnregister: true,
      defaultValues: {
        createAccount: true,
      },
    });

    const handlePlaceOrder = async (
      payPalFormData: PayPalFormData
    ): Promise<void> => {
      cart.setUpdatingCart(true);

      if (payPalFormData.customFields) {
        Object.entries(payPalFormData.customFields).forEach(([key, value]) => {
          const fieldNameArr = key.split('_');
          const id = fieldNameArr[fieldNameArr.length - 1];
          cart.updateCustomField(Number(id), value);
        });
      }

      if (rootStore.invalidCountryShippingAddress) {
        setFormErrors([UIConstants.INVALID_COUNTRY_ERROR]);
        cart.setUpdatingCart(false);
        return;
      }

      if (rootStore.invalidStateShippingAddress) {
        setFormErrors([UIConstants.INVALID_STATE_ERROR]);
        cart.setUpdatingCart(false);
        return;
      }

      let reCaptchaStatus = 400;

      if (
        reCaptchaRef.current &&
        configuration.reCaptcha.checkoutEnabled &&
        configuration.reCaptcha.size === 'invisible'
      ) {
        cart.setUpdatingCart(false);
        cart.updateSubmitToken();
        const reCaptchaValue = await reCaptchaRef.current.executeAsync();
        if (reCaptchaValue) {
          cart.setUpdatingCart(true);
          reCaptchaStatus = await configuration.verifyReCaptchaFlow(
            reCaptchaValue
          );
        }
        reCaptchaRef.current.reset();
        if (reCaptchaStatus !== 200) {
          cart.setUpdatingCart(false);
          setFormErrors([UIConstants.RECAPTCHA_ERROR]);
          onScrollTopOfPage();
          return;
        }
        cart.updateSubmitToken(reCaptchaValue);
      }

      setFormErrors([]);
      let orderId: string | undefined;

      try {
        if (!customer.isLoggedIn) {
          customer.resetToGuestDefaults();
          customer.setPassword(payPalFormData.password ?? null);
          customer.setNewCustomerData(cart.cartData);
          const customerId = await customer.saveNewCustomer();

          cart.customer.updateId(customerId);
        }
        const orderResponse = await cart.placeOrder();
        orderId = orderResponse.orderId;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.isValidationError) {
          handleApiValidationError(error);
        } else {
          handleGenericError(error);
        }
      }
      if (orderId) {
        try {
          await customer.saveCustomerAddresses();
        } catch (error) {
          // TODO: Do something with this caught error other than logging
          // eslint-disable-next-line no-console
          console.error(error);
        }
        rootStore.send({ type: 'ORDER_PLACED', orderId });
      }
      cart.setUpdatingCart(false);
    };

    return (
      <>
        <SectionTitle>Checkout: Final Step</SectionTitle>
        {rootStore.stateMatches('PayPal.UserStatus.Registered') && (
          <div className="mx-2 text-sm">
            Review, and click pay. Or{' '}
            <InternalLink onClick={handleStartNewCheckoutClicked}>
              start a new checkout
            </InternalLink>
            .
          </div>
        )}
        {!!configuration.messages.oneClick && (
          <CustomMessage customMessage={configuration.messages.oneClick} />
        )}
        <FormErrorLabel errors={formErrors} />
        {rootStore.stateMatches('PayPal.UserStatus.Guest') ? (
          <>
            <GuestContactSection
              defaultEmailAddress={cart.customer.emailAddress ?? undefined}
              onValidData={handleValidGuestContactSection}
              onErrorStatus={setHasContactInfoError}
              isEmailRegistered={
                !configuration.features.disableCustomerLogin &&
                rootStore.isEmailRegistered
              }
              tryCustomerLoginAsync={
                !configuration.features.disableCustomerLogin
                  ? handleTryCustomerLoginAsync
                  : undefined
              }
              onLoginSuccess={handleLoginSuccess}
              loginRequired={isLoginRequired}
              disableEmailAutoFocus
            />

            {configuration.features.enableEmailOptIn && (
              <LabeledCheckbox
                name="emailOptIn"
                renderChild
                label={
                  configuration.labels.emailOptIn ??
                  'Send me exclusive offers via email'
                }
                defaultChecked
                onChange={handleEmailOptInOnChange}
              />
            )}
          </>
        ) : (
          <AuthenticatedContactSection
            emailAddress={cart.customer.emailAddress as string}
            onLogout={onLogout}
            groupPosition="First"
          />
        )}
        {rootStore.stateMatches('PayPal.Checkout.MissingFields') && (
          <div className="container px-2 flex flex-col pb-4">
            <SectionTitle>Shipping Address</SectionTitle>
            <AddressFormWithButtons
              defaultValues={cart.shippingAddress as AddressFormData}
              isShippingAddress
              countryOptions={countryOptions}
              getStateConfig={handleGetStateOptions}
              onSubmitClicked={handleAddressSave}
              onErrorStatus={setHasAddressError}
              missingFields={cart.missingRequiredFields}
              autoFocus
              shippingRatesAddressType={
                configuration.features.shippingRatesAddressType
              }
            />
          </div>
        )}
        {rootStore.stateMatches('PayPal.Checkout.ShippingEdit') && (
          <div className="container px-2 flex flex-col pb-4">
            <SectionTitle>Shipping Address</SectionTitle>
            <AddressFormWithButtons
              defaultValues={cart.shippingAddress as AddressFormData}
              isShippingAddress
              countryOptions={countryOptions}
              getStateConfig={handleGetStateOptions}
              onSubmitClicked={handleAddressSave}
              onErrorStatus={setHasAddressError}
              missingFields={['phoneNumber']}
              autoFocus
              displayCancelButton
              onCancelButtonClicked={handleShippingAddressEditCanceled}
              shippingRatesAddressType={
                configuration.features.shippingRatesAddressType
              }
            />
          </div>
        )}
        {rootStore.stateMatches('PayPal.Checkout.Start') && (
          <AuthenticatedShippingAddressSection
            shippingAddress={
              cart.shippingAddress as CustomerShippingAddressContract
            }
            groupPosition={
              rootStore.stateMatches('PayPal.UserStatus.Guest')
                ? 'First'
                : 'Between'
            }
            onShipToInfoEdit={handleShippingAddressEdit}
            disableActionButton={
              !rootStore.stateMatches('PayPal.Checkout.MissingFields')
            }
            toolTipText={
              !rootStore.stateMatches('PayPal.Checkout.MissingFields')
                ? UIConstants.DISABLED_ADDRESS_WITH_PAYPAL_TOOL_TIP
                : undefined
            }
            toolTipKey="PayPalShippingAddress"
          />
        )}
        <AuthenticatedPaymentSection
          paymentProfile={cart.cartData.paymentProfile}
          onPaymentEdit={onEditPayment}
          groupPosition="Between"
          appliedGiftCards={cart.cartData.giftCards}
        />
        <AuthenticatedBillingAddressSection
          displaySameAsShipping={cart.isBillingAddressSameAsShipping}
          billingAddress={cart.billingAddress as CustomerBillingAddressContract}
          groupPosition="Last"
          disallowEdit
        />
        <ShippingMethodSection onValidData={handleShippingMethodChanged} />
        <FormProvider {...payPalViewForm}>
          {process.env.NODE_ENV === 'development' && (
            <DevTool control={payPalViewForm.control} placement="top-right" />
          )}
          <form onSubmit={payPalViewForm.handleSubmit(handlePlaceOrder)}>
            <CustomFieldSection
              customFields={cart.cartData.customFields}
              useContext
            />
            {!configuration?.features?.disableCustomerLogin &&
              !rootStore.isEmailRegistered &&
              !customer.isLoggedIn && (
                <AccountCreateFormWithFormContext
                  needAccountToCheckout={
                    !configuration.features?.enableGuestCheckout
                  }
                  isEmailRegistered={rootStore.isEmailRegistered}
                />
              )}
            {configuration?.features?.enableOrderComments && (
              <OrderComment
                orderComment={cart.cartData.misc?.comment || ''}
                onOrderCommentUpdated={cart.updateComment}
              />
            )}
            {configuration?.reCaptcha?.checkoutEnabled && (
              <div className="flex items-center justify-center">
                <ReCAPTCHA
                  ref={reCaptchaRef}
                  sitekey={configuration?.reCaptcha?.siteKey as string}
                  size={configuration?.reCaptcha?.size as Size}
                  hl={configuration?.reCaptcha?.hl as string}
                  theme={configuration?.reCaptcha?.theme as Theme}
                  badge={configuration?.reCaptcha?.badge as Badge}
                  onExpired={expiredCallback}
                  onChange={changeCallback}
                />
              </div>
            )}
            {!rootStore.stateMatches('PayPal.Checkout.MissingFields') && (
              <Button
                center
                className="max-w-sm"
                isDisabled={
                  configuration?.reCaptcha?.checkoutEnabled &&
                  configuration?.reCaptcha?.size !== 'invisible' &&
                  !captchaResponse
                }
                isOtherActionPending={
                  cart.isTokenizingCard || cart.updatingCart
                }
              >
                {`Pay ${
                  configuration.formattedCurrencySymbol
                }${cart.grandTotal.toFixed(2)}`}
              </Button>
            )}
          </form>
        </FormProvider>
      </>
    );
  }
);

export default PayPalView;
